// Phase D2's HUD, world-space UI and map (gates D2.1 and D2.2; the hud lab, `?scene=hud`). On @rpg/ui:
//   - the HUD (health, the stamina bar while in use, the equipped weapon with its skill's cooldown, the level and its
//     experience, the objective line, a notice): Preact draws its structure once and again only when the structure
//     changes (the minimap's or the compass's slot, the touch overlay's room); every frame the values go straight to
//     the DOM through direct writers. It fades when idle and comes back on any change; awake in a fight;
//   - world-space UI: enemy plates with health bars (alert or hurt, within range), damage numbers from `combat:hit`,
//     speech bubbles from `dialogue:line` over the hero, a townsperson or a placement, markers (the objective,
//     clamped at the screen's edge with an arrow and dimmed behind walls; the places found, hidden behind walls) and
//     indicators at the screen's edge for attackers winding up off the screen. Occlusion is a ray through the terrain
//     and the solids from the camera, every few frames per anchor;
//   - the minimap (turning with the view, or north-up: the `hud.minimap` setting) and the map screen (M, the pad's
//     select, or a tap on the minimap), both over the map's picture under the fog of what isn't explored; the compass
//     strip from the same data with `?compass=1`; fast travel between reached waypoints through the discovery's action.
// The sources are the seams' (item 5): combat's view of the hero, rpg's `progress()`, story's
// `objective()` (a stand-in), the equipped weapon, the fight's enemies and attackers.
import * as THREE from "three";
import {
  cardinals,
  bearingTo,
  createCompass,
  createHud,
  createMinimap,
  createWorldUi,
  paintPicture,
  preactDiffs,
  UI_FAULTS,
  type Compass,
  type CompassItem,
  type Hud,
  type MapLayers,
  type MapMarker,
  type MapPanelHandle,
  type Minimap,
  type Settings,
  type Ui,
  type WorldPos,
  type WorldUi,
} from "@rpg/ui";
import { DISCOVERY_FAULTS } from "@rpg/world";
import { objectiveCompassItem, objectiveMapMarker } from "@rpg-unit/quest-journal";
import type { Runtime, Input } from "@rpg/runtime";
import type { TouchOverlay } from "@rpg/runtime/dom";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import type { FlowHandle } from "../boot";
import type { SimMount } from "../sim";
import { HERO_ID } from "../enemies/fight";
import { mapPicture, type PoiInfo } from "./content";
import { DISCOVERY_ACTIONS } from "./discovery";
import { mapScreen } from "./map-screen";
import type { Rpg } from "../rpg/module";

/** The HUD's planted defects, as `?faults=` and `__rpg.faults()` name them (each lives beside what it breaks: the
 *  HUD's and the world-space UI's in @rpg/ui, the discovery's in @rpg/world). */
export const HUD_FAULT_NAMES = ["hud-value-render", "world-ui-no-occlusion", "world-ui-no-clamp", "compass-mirrored", "discovery-cells-not-saved"] as const;

const FAULT_SWITCH: Record<(typeof HUD_FAULT_NAMES)[number], { get(): boolean; set(on: boolean): void }> = {
  "hud-value-render": { get: () => UI_FAULTS.hudValueRender, set: (on) => void (UI_FAULTS.hudValueRender = on) },
  "world-ui-no-occlusion": { get: () => UI_FAULTS.worldUiIgnoreOcclusion, set: (on) => void (UI_FAULTS.worldUiIgnoreOcclusion = on) },
  "world-ui-no-clamp": { get: () => UI_FAULTS.worldUiNoClamp, set: (on) => void (UI_FAULTS.worldUiNoClamp = on) },
  "compass-mirrored": { get: () => UI_FAULTS.compassMirrored, set: (on) => void (UI_FAULTS.compassMirrored = on) },
  "discovery-cells-not-saved": { get: () => DISCOVERY_FAULTS.cellsNotSaved, set: (on) => void (DISCOVERY_FAULTS.cellsNotSaved = on) },
};

/** The group for src/faults.ts's joined record. */
export const hudFaults: FaultGroup = {
  names: HUD_FAULT_NAMES,
  get: (n) => FAULT_SWITCH[n as (typeof HUD_FAULT_NAMES)[number]]?.get() ?? false,
  set: (n, on) => FAULT_SWITCH[n as (typeof HUD_FAULT_NAMES)[number]]?.set(on),
};

/** Turn on the defects `?faults=` names. */
export function applyHudFaultSwitches(search = typeof location !== "undefined" ? location.search : ""): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of HUD_FAULT_NAMES) if (on.includes(n)) FAULT_SWITCH[n].set(true);
}

/** What the HUD needs from the game. */
export interface HudDeps {
  runtime: Runtime;
  camera: THREE.PerspectiveCamera;
  content: SpikeContent;
  sim: SimMount;
  ui: Ui;
  settings: Settings;
  flow: FlowHandle;
  input: Input;
  overlay: TouchOverlay;
  search?: string;
  /** Phase D joined: the rpg layer (its `progress()` for the level and experience). */
  rpg: () => Rpg;
  /** Where an NPC stands (the town's townsfolk, by id), for its speech bubble; null when it isn't one or is indoors. */
  npcAt: (id: string) => readonly [number, number, number] | null;
  /** Whether a cutscene is playing (the HUD and the world-space UI hide under it). */
  cutscene: () => boolean;
  /** Phase D joined: the story's `objective()` (seam 5): the tracked quest's objective, its label (a string id) and
   *  where its marker points; null with none (or no story: `?story=off`). */
  objective: () => { label: string; pos: readonly [number, number, number] | null } | null;
}

/** The HUD table's row (content/tables/hud.json). */
export const HUD_ROW = "play";

/** Mount the HUD, the world-space UI, the minimap and the map screen on the hud lab. Returns the handle's keys. */
export function mountHud(d: HudDeps) {
  const hudSim = d.sim.hud;
  if (!hudSim) throw new Error("hud: the scene has no hud parts (?scene=hud)");
  applyHudFaultSwitches(d.search);
  const hw = hudSim.world;
  const t = d.content.t;
  const reg = d.content.registry;
  const q = new URLSearchParams(d.search ?? location.search);
  const m = (): ReturnType<typeof hw.mount> => hw.mount();
  const fight = () => m().fight();
  const rig = () => m().rig();
  const disc = () => hw.discovery();
  const map = hw.map();
  const row = reg.get("hud", HUD_ROW);
  if (!row) throw new Error(`hud: no hud row "${HUD_ROW}"`);
  const { plateRange: PLATE_RANGE, poiRange: POI_MARKER_RANGE, bubbleRange: BUBBLE_RANGE, damageLife: DAMAGE_LIFE } = row.world;
  const NOTICE_S = row.notice;

  // the sources (seam 5, phase D joined): the rpg layer's progress() and the story's objective() (the marker a little
  // above the ground it points at)
  const progress = { progress: () => d.rpg().progress() };
  const objective = {
    objective: (): { label: string; pos: [number, number, number] | null } | null => {
      const o = d.objective();
      return o ? { label: o.label, pos: o.pos ? [o.pos[0], o.pos[1] + 1.6, o.pos[2]] : null } : null;
    },
  };

  // the HUD
  const touchShown = (): boolean => d.overlay.shown;
  const hud: Hud = createHud({
    t,
    labels: { health: "hud.health", stamina: "hud.stamina", value: "hud.value", level: "hud.level", xp: "hud.xp", skillReady: "hud.skillReady", cooldown: "hud.cooldown", objective: "hud.objective" },
    structure: { minimap: true, compass: q.get("compass") === "1", touch: touchShown() },
    idle: row.idle,
    touchRoom: row.touch,
  });
  // the HUD follows the UI's text scale and reduce motion (the settings apply them to the UI's root)
  const followUi = (): void => {
    hud.setTextScale(Number(getComputedStyle(d.ui.root).getPropertyValue("--rpg-text-scale")) || 1);
    hud.setReduceMotion(d.ui.root.hasAttribute("data-reduce-motion"));
  };
  followUi();
  d.settings.subscribe(followUi);

  // the map's layers: the picture painted once, the fog from the discovery
  const picture = mapPicture(m().world, map.row);
  const layers: MapLayers = { picture, base: paintPicture(document, picture, map.row.screen.paint), pxPerM: map.row.screen.paint, fog: null, fogCell: map.row.cell };
  const fog = () => disc().discovery;
  const minimap: Minimap = createMinimap({ layers, fog: fog(), fogColour: map.row.fog as [number, number, number, number], size: map.row.minimap.size, range: map.row.minimap.range, rotate: d.settings.get("hud.minimap") !== "north" });
  minimap.el.setAttribute("role", "button");
  minimap.el.setAttribute("aria-label", t("action.map"));
  minimap.el.style.pointerEvents = "auto";
  minimap.el.style.cursor = "pointer";
  minimap.el.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
    openMap();
  });
  d.settings.subscribe(() => minimap.setRotate(d.settings.get("hud.minimap") !== "north"));
  const compass: Compass = createCompass({});
  const attach = (): void => {
    hud.slot("minimap")?.appendChild(minimap.el);
    hud.slot("compass")?.appendChild(compass.el);
  };
  attach();
  hud.onStructure(attach);

  // world-space UI
  const cam = d.camera;
  const v = new THREE.Vector3();
  const project = (p: WorldPos) => {
    v.set(p[0], p[1], p[2]).applyMatrix4(cam.matrixWorldInverse);
    const depth = -v.z;
    v.set(p[0], p[1], p[2]).project(cam);
    return { x: v.x, y: v.y, depth };
  };
  const camPos = new THREE.Vector3();
  let rays = 0;
  const occluded = (p: WorldPos): boolean => {
    cam.getWorldPosition(camPos);
    const dir = { x: p[0] - camPos.x, y: p[1] - camPos.y, z: p[2] - camPos.z };
    const len = Math.hypot(dir.x, dir.y, dir.z);
    if (len < 0.5) return false;
    rays++;
    const hit = rig().collision.castRay({ x: camPos.x, y: camPos.y, z: camPos.z }, dir, len, { layers: ["terrain", "solid"] });
    return !!hit && hit.distance < len - 0.4;
  };
  const wui: WorldUi = createWorldUi({ project, occluded, inset: row.world.edgeInset, occlusionEvery: row.world.occlusionEvery });

  // damage numbers from combat's hits
  const kindHeight = (kind: string): number => (reg.get("enemies", kind) as { body?: { height?: number } } | undefined)?.body?.height ?? 1.6;
  d.sim.events.on("combat:hit", (e) => {
    if (!(e.damage > 0)) return;
    wui.pop({ kind: "damage", pos: [e.point[0], e.point[1] + 0.35, e.point[2]], text: String(Math.round(e.damage)), life: DAMAGE_LIFE, ...(e.target === HERO_ID ? { tone: "hurt" } : e.crit ? { tone: "crit" } : {}) });
  });
  // speech bubbles from `dialogue:line` (story's, the town's, a scene's): over the hero, a townsperson (by NPC id) or a
  // layout placement; the narrator's lines are subtitles only (accessibility's)
  const speakerAt = (speaker: string): WorldPos | null => {
    if (speaker === "hero") {
      const h = rig().hero();
      return [h.feet.x, h.feet.y + 2.1, h.feet.z];
    }
    const n = d.npcAt(speaker);
    if (n) return [n[0], n[1] + 2.2, n[2]];
    const p = m().world.placements().find((x) => x.id === speaker);
    return p ? [p.pos[0], p.y + 2.4, p.pos[1]] : null;
  };
  d.sim.events.on("dialogue:line", (l) => {
    if (l.speaker === "narrator") return;
    const text = l.text || t(l.stringId);
    const life = l.seconds > 0 ? l.seconds : 1.2 + 0.045 * text.length;
    wui.set(`bubble:${l.speaker}`, { kind: "bubble", pos: () => speakerAt(l.speaker), text, life, maxDistance: BUBBLE_RANGE });
  });
  // a level reached: the notice area's (phase D joined: the rpg layer's level-up notice)
  d.sim.events.on("progress:level", (p) => hud.notify(t("notice.levelUp", { level: p.level }), NOTICE_S));
  // a place found: a notice (seen, or reached without being seen first); a waypoint reached says so
  const noticed = new Set<string>();
  d.sim.events.on("world:discovered", (e) => {
    const p = map.pois.find((x) => x.id === e.poi);
    if (!p) return;
    if (e.mark === "reached" && p.waypoint) hud.notify(t("hud.waypoint", { place: t(p.name) }), NOTICE_S);
    else if (!noticed.has(e.poi)) hud.notify(t("hud.discovered", { place: t(p.name) }), NOTICE_S);
    noticed.add(e.poi);
  });

  const poiInfo = (id: string): PoiInfo => map.pois.find((p) => p.id === id)!;
  /** The minimap's and the map screen's markers: places found (seen: faint), the objective, alert enemies. */
  const markers = (withEnemies: boolean): MapMarker[] => {
    const out: MapMarker[] = [];
    const dd = disc().discovery;
    for (const p of map.pois) {
      const mark = dd.markOf(p.id);
      if (!mark) continue;
      out.push({ id: p.id, x: p.x, z: p.z, icon: p.waypoint ? "waypoint" : "poi", dim: mark === "seen" });
    }
    const ob = objective.objective();
    if (ob?.pos) out.push(objectiveMapMarker(ob.pos));
    if (withEnemies) for (const e of fight().state().enemies) if (e.alive && e.percept.level === "alert") out.push({ id: `enemy:${e.id}`, x: e.pos[0], z: e.pos[2], icon: "enemy" });
    return out;
  };

  // the map screen, on the UI's stack over the paused game
  let mapPanel: MapPanelHandle | null = null;
  let mapFromPlay = false;
  const closeMap = (): void => {
    if (d.ui.top() === "map") d.ui.pop();
    if (mapFromPlay) {
      mapFromPlay = false;
      d.flow.resume();
    }
  };
  const travel = (to: string): string | null => {
    hudSim.dispatch(DISCOVERY_ACTIONS.travel, { to });
    const r = disc().state().lastTravel;
    if (r?.ok) {
      hud.notify(t("map.travelled", { place: t(poiInfo(to).name) }), NOTICE_S);
      return null;
    }
    const why: Record<string, string> = { fight: "map.refused.fight", indoors: "map.refused.indoors", "same-place": "map.refused.samePlace" };
    return why[r?.reason ?? ""] ?? "map.refused.notReached";
  };
  d.ui.addScreen(
    "map",
    mapScreen({
      layers: () => layers,
      fog,
      map: () => map,
      markers: () => {
        const h = rig().hero();
        return [...markers(false), { id: "player", x: h.feet.x, z: h.feet.z, icon: "player", heading: heading() }];
      },
      hero: () => ({ x: rig().hero().feet.x, z: rig().hero().feet.z }),
      found: () => ({ found: disc().discovery.pois.filter((p) => disc().discovery.markOf(p.id)).length, total: map.pois.length, explored: disc().discovery.fraction() }),
      waypoints: () => disc().discovery.waypoints().map((p) => poiInfo(p.id)),
      travel,
      close: closeMap,
      bind: (h) => void (mapPanel = h),
    }),
  );
  /** Open the map: from play it pauses first (the pause screen under it; closing resumes). */
  const openMap = (): void => {
    if (d.ui.top() === "map") return;
    if (d.flow.state() === "play" && d.ui.top() === null) {
      mapFromPlay = true;
      d.flow.pause();
    }
    if (d.flow.state() === "pause" || d.ui.top() !== null) d.ui.push("map");
  };
  d.input.contexts.on("map", openMap);

  /** The camera's heading: radians clockwise from north (−z) on the map. */
  const fwd = new THREE.Vector3();
  const heading = (): number => {
    cam.getWorldDirection(fwd);
    return Math.atan2(fwd.x, -fwd.z);
  };

  // visible in play, with no screen over it
  const syncVisible = (): void => {
    const on = d.flow.state() === "play" && d.ui.top() === null && !d.cutscene();
    hud.setVisible(on);
    wui.setVisible(on);
  };
  d.flow.on(syncVisible);
  d.ui.stack.onChange(syncVisible);
  syncVisible();

  // every drawn frame, after the draw (the camera's matrices are this frame's)
  let now = 0;
  let frames = 0;
  const live = new Set<string>();
  d.runtime.on(
    "render",
    (c) => {
      // the UI runs on real time: the fader on the whole frame, the anchors' animation on at most 0.1 s of it
      const dt = Math.min(0.1, Math.max(0, c.realDt));
      now += Math.max(0, c.realDt);
      frames++;
      syncVisible();
      hud.setStructure({ touch: touchShown() });
      const f = fight();
      const hero = f.view(HERO_ID);
      const weapon = hero?.weapon ? (reg.get("weapons", hero.weapon) as { label: { id: string }; cooldown: number } | undefined) : undefined;
      const pr = progress.progress();
      const ob = objective.objective();
      const engaged = f.engaged();
      hud.update(
        {
          hp: hero?.hp ?? 0,
          hpMax: hero?.hpMax ?? 1,
          stamina: hero?.stamina ?? 0,
          staminaMax: hero?.staminaMax ?? 1,
          weapon: weapon?.label.id ?? null,
          cooldown: hero?.cooldown ?? 0,
          cooldownMax: weapon?.cooldown ?? 1,
          level: pr.level,
          xp: pr.xp,
          xpNext: pr.next,
          objective: ob?.label ?? null,
          awake: engaged,
        },
        now,
      );
      // plates: enemies alert or hurt, within range of the hero
      const h = rig().hero();
      live.clear();
      for (const e of f.state().enemies) {
        if (!e.alive) continue;
        const fv = f.view(e.id);
        if (!fv) continue;
        const near = Math.hypot(e.pos[0] - h.feet.x, e.pos[2] - h.feet.z) <= PLATE_RANGE;
        if (!near || (e.percept.level !== "alert" && fv.hp >= fv.hpMax)) continue;
        const id = `plate:${e.id}`;
        live.add(id);
        const ht = kindHeight(e.kind) + 0.45;
        const name = (reg.get("enemies", e.kind) as { name: { id: string } } | undefined)?.name.id;
        wui.set(id, { kind: "plate", pos: [e.pos[0], e.pos[1] + ht, e.pos[2]], text: name ? t(name) : "", bar: fv.hpMax > 0 ? fv.hp / fv.hpMax : 0, maxDistance: PLATE_RANGE + 10 });
      }
      // attackers winding up or striking, off the screen: an arrow at the edge (accessibility's setting turns them off)
      if (d.settings.get("accessibility.attackerIndicators") !== false) for (const id of f.attackers()) {
        const e = f.state().enemies.find((x) => x.id === id);
        if (!e) continue;
        const key = `attacker:${id}`;
        live.add(key);
        wui.set(key, { kind: "indicator", pos: [e.pos[0], e.pos[1] + 1, e.pos[2]], clamp: true, occlusion: "none" });
      }
      // the objective's marker (clamped at the edge, dimmed behind walls) and the places found nearby (hidden behind)
      // (accessibility's objective-marker setting turns it off)
      if (ob?.pos && d.settings.get("accessibility.objectiveMarker") !== false) {
        live.add("objective");
        const dist = Math.round(Math.hypot(ob.pos[0] - h.feet.x, ob.pos[2] - h.feet.z));
        wui.set("objective", { kind: "marker", pos: ob.pos, text: t("hud.distance", { m: dist }), clamp: true, occlusion: "dim", tone: "objective" });
      }
      const dd = disc().discovery;
      for (const p of map.pois) {
        if (!dd.markOf(p.id) || Math.hypot(p.x - h.feet.x, p.z - h.feet.z) > POI_MARKER_RANGE) continue;
        // the objective's own place has the objective's marker
        if (ob?.pos && Math.hypot(p.x - ob.pos[0], p.z - ob.pos[2]) < 2) continue;
        const key = `poi:${p.id}`;
        live.add(key);
        wui.set(key, { kind: "marker", pos: [p.x, m().world.heightAt(p.x, p.z) + 2.2, p.z], text: t(p.name), tone: "poi", maxDistance: POI_MARKER_RANGE + 10 });
      }
      for (const s of wui.state()) if ((s.kind === "plate" || s.kind === "indicator" || s.kind === "marker") && !live.has(s.id) && !s.id.startsWith("pin:")) wui.remove(s.id);
      wui.frame(dt);
      // the minimap and the compass
      const hd = heading();
      minimap.frame({ x: h.feet.x, z: h.feet.z, heading: hd }, markers(true));
      if (hud.slot("compass")) compass.frame(hd, compassItems(h.feet.x, h.feet.z));
    },
    { name: "hud: frame", order: 1e6 },
  );

  const compassItems = (x: number, z: number): CompassItem[] => {
    const items = cardinals({ n: t("compass.n"), e: t("compass.e"), s: t("compass.s"), w: t("compass.w") });
    const dd = disc().discovery;
    for (const p of map.pois) if (dd.markOf(p.id)) items.push({ id: p.id, bearing: bearingTo(x, z, p.x, p.z), label: p.waypoint ? "◆" : "•", tone: p.waypoint ? "waypoint" : "poi" });
    const ob = objective.objective();
    if (ob?.pos) items.push(objectiveCompassItem(x, z, ob.pos));
    return items;
  };

  const handle = {
    hud: {
      /** The HUD's structure renders (Preact) and the page's Preact diffs, its direct writes, and what it shows. */
      renders: () => hud.renders(),
      diffs: () => preactDiffs(),
      writes: () => hud.writes(),
      state: () => hud.state(),
      frames: () => frames,
      /** The world-space anchors' last placements, the rays cast, the layer's writes. */
      world: () => wui.state(),
      rays: () => rays,
      worldWrites: () => wui.writes(),
      /** A spec's own anchor (`pin:` + id): a plate, marker, indicator or bubble at a world point, kept until unpinned. */
      pin: (id: string, kind: "plate" | "marker" | "indicator" | "bubble", x: number, y: number, z: number, o: { clamp?: boolean; occlusion?: "hide" | "dim" | "none"; text?: string } = {}) =>
        wui.set(`pin:${id}`, { kind, pos: [x, y, z], ...(o.text !== undefined ? { text: o.text } : {}), ...(o.clamp !== undefined ? { clamp: o.clamp } : {}), ...(o.occlusion ? { occlusion: o.occlusion } : {}) }),
      unpin: (id: string) => wui.remove(`pin:${id}`),
      /** The fight's attackers now (in a wind-up or a strike). */
      attackers: () => fight().attackers(),
      /** The enemies: id, kind, alive, where, how alert. */
      enemies: () => fight().state().enemies.map((e) => ({ id: e.id, kind: e.kind, alive: e.alive, pos: [...e.pos], level: e.percept.level })),
      /** Say a line on the bus as dialogue does (`dialogue:line`; a bubble over `speaker`: "hero", an NPC id or a
       *  placement id). */
      say: (speaker: string, stringId: string, seconds = 0) => d.sim.events.emit("dialogue:line", { speaker, stringId, text: t(stringId), seconds }),
      /** Gain experience (the rpg layer's, as a defeat grants it). */
      gain: (xp: number) => void d.rpg().grantXp(xp, "test"),
      progress: () => progress.progress(),
      objective: () => objective.objective(),
      setStructure: (s: { minimap?: boolean; compass?: boolean; touch?: boolean }) => hud.setStructure(s),
      /** Project a world point as the world-space UI does. */
      project: (x: number, y: number, z: number) => project([x, y, z]),
      occluded: (x: number, y: number, z: number) => occluded([x, y, z]),
      heading,
      minimap: () => minimap.state(),
      compass: () => compass.state(),
      compassItems: () => {
        const h = rig().hero();
        return compassItems(h.feet.x, h.feet.z);
      },
    },
    map: {
      /** The discovery: revealed share and count, the marks, the log, the last travel. */
      state: () => {
        const s = disc().state();
        const dd = disc().discovery;
        return { explored: dd.fraction(), cells: dd.count(), marks: { ...s.discovery.marks }, order: [...s.discovery.order], lastTravel: s.lastTravel, travels: s.travels, step: s.step, at: disc().at(), discovery: structuredClone(s.discovery) };
      },
      pois: () => map.pois.map((p) => ({ ...p })),
      waypoints: () => disc().discovery.waypoints().map((p) => p.id),
      log: () => [...disc().log],
      revealed: (x: number, z: number) => disc().discovery.revealedAt(x, z),
      /** Travel through the sim's action (as the map screen's buttons do); the refusal's string id or null. */
      travel,
      /** Mark a place found (a quest's reveal) through the sim. */
      mark: (poi: string, mark: "seen" | "reached" = "seen") => hudSim.dispatch(DISCOVERY_ACTIONS.mark, { poi, mark }),
      /** Reveal the cells within `radius` of (x, z) through the sim (a bought map's reveal). */
      reveal: (x: number, z: number, radius: number) => hudSim.dispatch(DISCOVERY_ACTIONS.reveal, { x, z, radius }),
      open: openMap,
      close: closeMap,
      panel: () => (mapPanel ? { view: mapPanel.view(), draws: mapPanel.draws() } : null),
      pan: (dx: number, dy: number) => mapPanel?.pan(dx, dy),
      zoom: (f: number) => mapPanel?.zoom(f),
      markers: () => markers(true),
    },
  };
  return { handle, hud, wui, minimap };
}
